import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';

/**
 * Pulls the most useful human-readable error message from a DRF / Axios error.
 * Falls back to the supplied default when nothing useful is found.
 */
function extractApiError(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as
      | { detail?: string; error?: string; message?: string; non_field_errors?: string[] }
      | string
      | undefined;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      if (typeof data.detail === 'string' && data.detail) return data.detail;
      if (typeof data.error === 'string' && data.error) return data.error;
      if (typeof data.message === 'string' && data.message) return data.message;
      if (Array.isArray(data.non_field_errors) && data.non_field_errors.length > 0) {
        return data.non_field_errors[0];
      }
      // First field-level error string we can find
      for (const key of Object.keys(data)) {
        const v = (data as Record<string, unknown>)[key];
        if (typeof v === 'string' && v) return `${key}: ${v}`;
        if (Array.isArray(v) && v.length && typeof v[0] === 'string') return `${key}: ${v[0]}`;
      }
    }
    if (err.message) return err.message;
  }
  return fallback;
}

export interface PhoneBlacklistEntry {
  id: number;
  phoneNumber: string;
  reason: string | null;
  blacklistedAt: string;
  blacklistedBy: { id: number; email: string } | null;
  person: {
    id: number;
    fullName: string | null;
    phoneNumber: string | null;
    place: { id: string; displayName: string } | null;
  } | null;
}

export interface PhoneBlacklistResponse {
  items: PhoneBlacklistEntry[];
  total: number;
  stats: { total: number; linkedToPerson: number; orphan: number };
}

export function usePhoneBlacklist(search = '', offset = 0) {
  return useQuery({
    queryKey: ['phone-blacklist', search, offset],
    queryFn: async () => {
      const params = new URLSearchParams({ offset: String(offset), limit: '20' });
      if (search) params.set('search', search);
      const { data } = await api.get<PhoneBlacklistResponse>(
        `${API_ROUTES.phoneBlacklist}?${params}`,
      );
      return data;
    },
    staleTime: 30_000,
  });
}

export function useAddPhoneToBlacklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      phoneNumbers?: string[];
      personIds?: number[];
      reason?: string;
    }) => {
      const { data } = await api.post(API_ROUTES.phoneBlacklist, payload);
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['phone-blacklist'] });
      qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      const added: number = data.added ?? 0;
      const skipped: number = data.skipped ?? 0;
      if (added > 0) {
        const msg =
          skipped > 0
            ? `${added} number${added !== 1 ? 's' : ''} added (${skipped} already blocked)`
            : `${added} number${added !== 1 ? 's' : ''} added to blacklist`;
        toast.success(msg, { duration: 3000 });
      } else if (skipped > 0) {
        toast('All numbers were already blacklisted', { icon: 'ℹ️', duration: 3000 });
      } else {
        toast('No valid numbers to add', { icon: 'ℹ️', duration: 3000 });
      }
    },
    onError: (err) => {
      toast.error(extractApiError(err, "Couldn't add to blacklist. Please try again."), {
        duration: 8000,
      });
    },
  });
}

export function useRemovePhoneFromBlacklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { ids?: number[]; phoneNumbers?: string[] }) => {
      const { data } = await api.delete(API_ROUTES.phoneBlacklist, { data: payload });
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['phone-blacklist'] });
      qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      const removed: number = data.removed ?? 0;
      toast.success(`${removed} number${removed !== 1 ? 's' : ''} removed from blacklist`, {
        duration: 3000,
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, "Couldn't update blacklist. Please try again."), {
        duration: 8000,
      });
    },
  });
}

export function useBlacklistConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, reason }: { conversationId: string; reason?: string }) => {
      const { data } = await api.post(
        `/api/v1/whatsapp/conversations/${conversationId}/blacklist/`,
        { reason },
      );
      return data;
    },
    onMutate: async ({ conversationId }) => {
      await qc.cancelQueries({ queryKey: ['whatsapp-conversation', conversationId] });
      const prev = qc.getQueryData(['whatsapp-conversation', conversationId]);
      qc.setQueryData(['whatsapp-conversation', conversationId], (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        return { ...(old as object), isPhoneBlacklisted: true };
      });
      return { prev };
    },
    onError: (err, { conversationId }, context) => {
      if (context?.prev) qc.setQueryData(['whatsapp-conversation', conversationId], context.prev);
      qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      toast.error(extractApiError(err, "Couldn't block this number. Try again."), {
        duration: 8000,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['phone-blacklist'] });
      qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      toast.success('Number blocked. They will no longer receive messages.', { duration: 3000 });
    },
  });
}

export function useUnblacklistConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId }: { conversationId: string }) => {
      const { data } = await api.delete(
        `/api/v1/whatsapp/conversations/${conversationId}/blacklist/`,
      );
      return data;
    },
    onMutate: async ({ conversationId }) => {
      await qc.cancelQueries({ queryKey: ['whatsapp-conversation', conversationId] });
      const prev = qc.getQueryData(['whatsapp-conversation', conversationId]);
      qc.setQueryData(['whatsapp-conversation', conversationId], (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        return { ...(old as object), isPhoneBlacklisted: false, phoneBlacklistReason: null };
      });
      return { prev };
    },
    onError: (err, { conversationId }, context) => {
      if (context?.prev) qc.setQueryData(['whatsapp-conversation', conversationId], context.prev);
      toast.error(extractApiError(err, "Couldn't unblock this number. Try again."), {
        duration: 8000,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['phone-blacklist'] });
      qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      toast.success('Number removed from blacklist.', { duration: 3000 });
    },
  });
}
